/**
 * 本地存储工具类
 * 提供统一的本地存储方法，包含 token 管理
 */

/**
 * Token 存储键名
 */
import {
    TOKEN_KEY,
    USER_INFO_KEY,
    UDID_KEY,
    APP_ID_KEY,
    IS_LOGGED_IN_KEY,
    IS_COMPLETE_INFO_KEY,
    SEARCH_HISTORY_KEY,
    SERVICE_TIME_KEY,
    IS_EDIT_INFO_KEY,
    DETAIL_INFO_KEY,
    CHAT_EXPENSES_KEY,
    IS_EMBEDDED_H5_KEY,
    IS_THINKING_KEY,
    MSG_IMG_COST_KEY,
    DEVICE_INFO_KEY,
  } from "@/utils/constant";

  
  /**
   * Token 存储工具
   */
  export const tokenStorage = {
    /**
     * 设置 token
     * @param token - 要存储的 token
     */
    setToken(token: string): void {

        localStorage.setItem(TOKEN_KEY, token);

    },
  
    /**
     * 获取 token
     * @returns token 或 null
     */
    getToken(): string | null {

        return localStorage.getItem(TOKEN_KEY);

    },
  
    /**
     * 移除 token
     */
    removeToken(): void {

        localStorage.removeItem(TOKEN_KEY);

    },
  
    /**
     * 检查是否存在 token
     * @returns 是否存在 token
     */
    hasToken(): boolean {
      return !!this.getToken();
    },
  };
  
  /**
   * 用户信息存储工具
   */
  export const userStorage = {
    /**
     * 是否完善信息
     * @param isCompleteInfo - 是否完善信息
     */
    setIsCompleteInfo(isCompleteInfo: boolean): void {
      storage.setItem(IS_COMPLETE_INFO_KEY, isCompleteInfo);
    },
    /**
     * 获取是否完善信息
     * @returns 是否完善信息
     */
    getIsCompleteInfo(): boolean {
      return storage.getItem<boolean>(IS_COMPLETE_INFO_KEY) || false;
    },
    /**
     * 移除是否完善信息
     */
    removeIsCompleteInfo(): void {
      storage.removeItem(IS_COMPLETE_INFO_KEY);
    },
    /**
     * 设置用户信息
     * @param userInfo - 用户信息
     */
    setTouristInfo(userInfo: any): void {
      storage.setItem(USER_INFO_KEY, userInfo);
    },
    /**
     * 获取用户信息
     * @returns 用户信息
     */
    getTouristInfo(): any {
      return storage.getItem(USER_INFO_KEY);
    },
    /**
     * 移除用户信息
     */
    removeTouristInfo(): void {
      storage.removeItem(USER_INFO_KEY);
    },
    /**
     * 设置用户登录标记
     * @param isLoggedIn - 是否已经登录
     */
    setIsLoggedIn(isLoggedIn: boolean): void {
      storage.setItem(IS_LOGGED_IN_KEY, isLoggedIn);
    },
    /**
     * 获取用户登录标记
     * @returns 是否已经登录
     */
    getIsLoggedIn(): boolean | null {
      return storage.getItem<boolean>(IS_LOGGED_IN_KEY);
    },
    /**
     * 移除用户登录标记
     */
    removeIsLoggedIn(): void {
      storage.removeItem(IS_LOGGED_IN_KEY);
    },
    /**
     * 设置服务时间
     * @param serviceTime - 服务时间
     */
    setServiceTime(serviceTime: number): void {
      storage.setItem(SERVICE_TIME_KEY, serviceTime);
    },
    /**
     * 获取服务时间
     * @returns 服务时间
     */
    getServiceTime(): number | null {
      return storage.getItem<number>(SERVICE_TIME_KEY);
    },
    /**
     * 移除服务时间
     */
    removeServiceTime(): void {
      storage.removeItem(SERVICE_TIME_KEY);
    },
    /**
     * 设置游客登录标记
     * @param hasLoggedIn - 是否已经进行过游客登录
     */
    setTouristLoginFlag(hasLoggedIn: boolean): void {
      storage.setItem("tourist_login_flag", hasLoggedIn);
    },
    /**
     * 获取游客登录标记
     * @returns 是否已经进行过游客登录
     */
    getTouristLoginFlag(): boolean {
      return storage.getItem<boolean>("tourist_login_flag") || false;
    },
    /**
     * 移除游客登录标记
     */
    removeTouristLoginFlag(): void {
      storage.removeItem("tourist_login_flag");
    },
    setBalance(balance: number): void {
      storage.setItem("balance", balance);
    },
    getBalance(): number {
      return storage.getItem<number>("balance") || 0;
    },
    removeBalance(): void {
      storage.removeItem("balance");
    },
    setMsgCost(msgCost: number): void {
      storage.setItem("msgCost", msgCost);
    },
    getMsgCost(): number {
      return storage.getItem<number>("msgCost") || 0;
    },
    removeMsgCost(): void {
      storage.removeItem("msgCost");
    },
    /**
     * 设置是否编辑信息
     * @param isEditInfo - 是否编辑信息
     */
    setIsEditInfo(isEditInfo: boolean): void {
      storage.setItem(IS_EDIT_INFO_KEY, isEditInfo);
    },
    /**
     * 获取是否编辑信息
     * @returns 是否编辑信息
     */
    getIsEditInfo(): boolean {
      return storage.getItem<boolean>(IS_EDIT_INFO_KEY) || false;
    },
    /**
     * 移除是否编辑信息
     */
    removeIsEditInfo(): void {
      storage.removeItem(IS_EDIT_INFO_KEY);
    },
    setDetailInfo(detailInfo: any): void {
      storage.setItem(DETAIL_INFO_KEY, detailInfo);
    },
    getDetailInfo(): any {
      return storage.getItem(DETAIL_INFO_KEY);
    },
    removeDetailInfo(): void {
      storage.removeItem(DETAIL_INFO_KEY);
    },
    /**
     * 设置单条聊天数据
     * @param chatExpenses - 单条聊天数据
     */
    setChatExpenses(chatExpenses: number): void {
      storage.setItem(CHAT_EXPENSES_KEY, chatExpenses);
    },
    /**
     * 获取单条聊天数据
     * @returns 单条聊天数据
     */
    getChatExpenses(): any {
      return storage.getItem(CHAT_EXPENSES_KEY);
    },
    setIsEmbeddedH5(isEmbeddedH5: boolean): void {
      storage.setItem(IS_EMBEDDED_H5_KEY, isEmbeddedH5);
    },
    getIsEmbeddedH5(): boolean {
      return storage.getItem<boolean>(IS_EMBEDDED_H5_KEY) || false;
    },
    setIsThinking(isThinking: boolean): void {
      storage.setItem(IS_THINKING_KEY, isThinking);
    },
    getIsThinking(): boolean {
      return storage.getItem<boolean>(IS_THINKING_KEY) || false;
    },
    removeIsThinking(){
      localStorage.removeItem(IS_THINKING_KEY)
    },
    setMsgImgCost(msgImgCost: number): void {
      storage.setItem(MSG_IMG_COST_KEY, msgImgCost);
    },
    getMsgImgCost(): number {
      return storage.getItem<number>(MSG_IMG_COST_KEY) || 0;
    },
    removeMsgImgCost(): void {
      storage.removeItem(MSG_IMG_COST_KEY);
    },
    setDeviceInfo(deviceInfo: any): void {
      storage.setItem(DEVICE_INFO_KEY, deviceInfo);
    },
    getDeviceInfo(): any {
      return storage.getItem(DEVICE_INFO_KEY);
    },
    removeDeviceInfo(): void {
      storage.removeItem(DEVICE_INFO_KEY);
    }
  };
  
  /**
   * 设备标识存储工具
   */
  export const deviceStorage = {
    /**
     * 设置设备唯一标识符
     * @param udid - 设备唯一标识符
     */
    setUdid(udid: string): void {
      storage.setItem(UDID_KEY, udid);
    },
  
    /**
     * 获取设备唯一标识符
     * @returns udid 或 null
     */
    getUdid(): string | null {
      return storage.getItem<string>(UDID_KEY);
    },
  
    /**
     * 移除设备唯一标识符
     */
    removeUdid(): void {
      storage.removeItem(UDID_KEY);
    },
  
    /**
     * 检查是否存在设备唯一标识符
     * @returns 是否存在 udid
     */
    hasUdid(): boolean {
      return !!this.getUdid();
    },
  };
  
  /**
   * 应用配置存储工具
   */
  export const appStorage = {
    /**
     * 设置应用ID
     * @param appId - 应用ID
     */
    setAppId(appId: string): void {
      storage.setItem(APP_ID_KEY, appId);
    },
  
    /**
     * 获取应用ID
     * @returns appId 或 null
     */
    getAppId(): string | null {
      return storage.getItem<string>(APP_ID_KEY);
    },
  
    /**
     * 移除应用ID
     */
    removeAppId(): void {
      storage.removeItem(APP_ID_KEY);
    },
  
    /**
     * 检查是否存在应用ID
     * @returns 是否存在 appId
     */
    hasAppId(): boolean {
      return !!this.getAppId();
    },
  };
  
  /**
   * 通用本地存储工具
   */
  export const storage = {
    /**
     * 设置存储项
     * @param key - 存储键
     * @param value - 存储值
     */
    setItem(key: string, value: any): void {
        try {
          const serializedValue =
            typeof value === "string" ? value : JSON.stringify(value);
          localStorage.setItem(key, serializedValue);
        } catch (error) {
          console.error("存储数据失败:", error);
        }
    },
  
    /**
     * 获取存储项
     * @param key - 存储键
     * @returns 存储值或 null
     */
    getItem<T = any>(key: string): T | null {
        try {
          const item = localStorage.getItem(key);
          if (item === null) return null;
  
          // 尝试解析 JSON，如果失败则返回原始字符串
          try {
            return JSON.parse(item);
          } catch {
            return item as T;
          }
        } catch (error) {
          console.error("读取数据失败:", error);
          return null;
        }
      return null;
    },
  
    /**
     * 移除存储项
     * @param key - 存储键
     */
    removeItem(key: string): void {
        localStorage.removeItem(key);
    },
  
    /**
     * 清空所有存储
     */
    clear(): void {
        localStorage.clear();
    },
  
    /**
     * 检查存储项是否存在
     * @param key - 存储键
     * @returns 是否存在
     */
    hasItem(key: string): boolean {
      return this.getItem(key) !== null;
    },
  };
  
  export const searchStorage = {
    setSearchList(searchList: string[]): void {
      storage.setItem(SEARCH_HISTORY_KEY, searchList);
    },
    getSearchList(): string[] {
      return storage.getItem<string[]>(SEARCH_HISTORY_KEY) || [];
    },
    removeSearchList(): void {
      storage.removeItem(SEARCH_HISTORY_KEY);
    },
  };
  